import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import ApiServices from "../../../../services/ApiServices";
import { CONTENT } from "../../../../constants/api-constants";
import { typeOfContentOptions } from "../../../Distributors/constants";
import { useLoading } from "../../../../hooks/useLoader";
import DocumentViewer from "../../../../common/DocumentViewer/DocumentViewer";
import XFDLViewer from "../../../../common/XFDLViewer/XFDLViewer";
import { AudioPreview } from "../../../../common/AudioPreview/AudioPreview";
import { VideoPreview } from "../../../../common/VideoPreview/VideoPreview";
import { getSecureRandomInt } from "../../../../utilities/secureRandom";

/* Same set used by AddEditContent.js Content Preview tab. */
const DOCUMENT_VIEWER_TYPES = new Set([4, 5, 6, 1, 8, 9, 3]);

/* Mirror of AddEditContent's msContent helper — Word/Excel/PowerPoint use
 * gcpFilePath. */
const buildMsDocument = (result) => {
  if (![4, 5, 6].includes(result.typeOfContent)) return null;
  if (!result.gcpFilePath) return [];
  return [
    {
      uri: result.gcpFilePath,
      fileName: result.name,
      fileType: typeOfContentOptions?.find(
        (item) => item.value === result.typeOfContent,
      )?.fileType,
    },
  ];
};

/* Mirror of AddEditContent's streamContent helper — PDF/JPG/Audio/Video use
 * fileStream as a base64 string. */
const buildStreamDocument = (result) => {
  if (![1, 8, 9, 3, 7, 10].includes(result.typeOfContent)) return null;
  if (!result.fileStream) return [];
  return [
    {
      uri: result.fileStream,
      fileName: result.name,
      fileType: typeOfContentOptions?.find(
        (item) => item.value === result.typeOfContent,
      )?.fileType,
    },
  ];
};

/**
 * ContentPreviewDialog
 * Read-only preview popup opened from the SelectContentDialog Content tab's
 * View icon. Fetches the full content record and reuses the same preview
 * logic AddEditContent.js applies on its Content Preview tab — DocumentViewer
 * for office/PDF/image, AudioPreview/VideoPreview for media, XFDLViewer for
 * XFDL JSON.
 */
const ContentPreviewDialog = ({ open, contentId, contentName, onClose }) => {
  const { showLoader, show } = useLoading();
  const [selectedDocument, setSelectedDocument] = useState([]);
  const [fileContent, setFileContent] = useState(null);
  const [typeOfContent, setTypeOfContent] = useState(null);
  const [displayName, setDisplayName] = useState("");

  const resetState = useCallback(() => {
    setSelectedDocument([]);
    setFileContent(null);
    setTypeOfContent(null);
    setDisplayName("");
  }, []);

  useEffect(() => {
    if (!open || !contentId) {
      resetState();
      return undefined;
    }
    let cancelled = false;

    const fetchDetail = async () => {
      try {
        showLoader(true);
        const response = await ApiServices.httpGet(`${CONTENT}/${contentId}`);
        if (cancelled) return;
        if (!response?.responseStatus?.isSuccess) {
          toast.error(
            response?.responseStatus?.error || "Internal server error",
          );
          return;
        }
        const result = response.result || {};
        setTypeOfContent(result.typeOfContent);
        setDisplayName(result.name || contentName || "");

        const ms = buildMsDocument(result);
        const stream = buildStreamDocument(result);
        if (ms !== null) {
          setSelectedDocument(ms);
        } else if (stream !== null) {
          setSelectedDocument(stream);
        } else {
          setSelectedDocument([]);
        }

        if (result.typeOfContent === 2) {
          setFileContent(result.xfdlJson ? JSON.parse(result.xfdlJson) : null);
        } else {
          setFileContent(null);
        }
      } catch (err) {
        if (!cancelled) toast.error("Internal server error");
      } finally {
        if (!cancelled) showLoader(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [open, contentId, contentName, showLoader, resetState]);

  const renderPreview = () => {
    if (typeOfContent === 7) {
      return <AudioPreview selectedDocument={selectedDocument} />;
    }
    if (typeOfContent === 10) {
      return <VideoPreview selectedDocument={selectedDocument} />;
    }
    if (
      DOCUMENT_VIEWER_TYPES.has(Number(typeOfContent)) &&
      selectedDocument.length > 0
    ) {
      return (
        <DocumentViewer
          disableHeader={true}
          tempId={getSecureRandomInt(1000) + 1}
          documents={selectedDocument}
          config={{
            noRenderer: {
              overrideComponent: <div>No Document uploaded</div>,
            },
          }}
          height={"auto"}
        />
      );
    }
    if (fileContent) {
      return (
        <XFDLViewer
          document={fileContent}
          fileName={displayName}
          from="preview"
        />
      );
    }
    if (show) return null;
    return (
      <div className="no-preview">
        <img
          src="/images/no-preview-available.png"
          alt="No document attached to preview"
        />
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="select-content-dialog select-content-dialog--nested"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        className="select-content-dialog__title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>
          Content Preview
          {displayName ? ` — ${displayName}` : ""}
        </span>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="select-content-dialog__content">
        {renderPreview()}
      </DialogContent>
    </Dialog>
  );
};

ContentPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  contentName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

ContentPreviewDialog.defaultProps = {
  contentId: null,
  contentName: "",
};

export default ContentPreviewDialog;
